import React from 'react';
import { ToastContainer as ToastifyContainer } from 'react-toastify';
import type { TypeOptions } from 'react-toastify';
import classNames from 'classnames';

import Icons from 'assets/icons';

interface ToastStyleConfig {
  container: string;
  component: string;
  progressBar: string;
  body: string;
}

const toastStyles: ToastStyleConfig = {
  container: 'sm:w-2/5 min-w-[356px] max-w-[500px]',
  component: 'rounded-lg p-4 border-t border-r border-navy-100',
  progressBar: 'rounded-bl-none',
  body: 'items-start pl-3 pr-0',
};

const toastTypeStyles = {
  error: {
    component: `text-critical-700 border-l-8 border-l-critical-700 ${toastStyles.component}`,
    closeButton: 'text-critical-700',
    progressBar: `bg-critical-700/50 ${toastStyles.progressBar}`,
  },
  info: {
    component: `text-navy-800 border-l-8 border-l-navy-800 ${toastStyles.component}`,
    closeButton: 'text-navy-800',
    progressBar: `bg-navy-800/50 ${toastStyles.progressBar}`,
  },
  success: {
    component: `text-lime-700 border-l-8 border-l-lime-700 ${toastStyles.component}`,
    closeButton: 'text-lime-700',
    progressBar: `bg-lime-700/50 ${toastStyles.progressBar}`,
  },
  warning: {
    component: `text-amber-500 border-l-8 border-l-amber-500 ${toastStyles.component}`,
    closeButton: 'text-amber-500',
    progressBar: `bg-amber-500/50 ${toastStyles.progressBar}`,
  },
  default: {
    component: `text-gray-700 border-l-8 border-l-gray-700 ${toastStyles.component}`,
    closeButton: 'text-gray-700',
    progressBar: `bg-gray-700/50 ${toastStyles.progressBar}`,
  },
};

type ToastStyleKey = 'component' | 'closeButton' | 'progressBar';

const getToastTypeStyles = (defaultClassName = '', type: TypeOptions = 'default', key: ToastStyleKey): string =>
  classNames(defaultClassName, toastTypeStyles[type]?.[key] ?? toastTypeStyles.default[key]);

interface CustomCloseButtonProps {
  closeToast?: (e: React.MouseEvent<HTMLElement>) => void;
  type?: TypeOptions;
}

const CustomCloseButton = ({ closeToast, type }: CustomCloseButtonProps): React.JSX.Element => {
  const closeButtonClass = getToastTypeStyles('', type, 'closeButton');

  return (
    <button onClick={closeToast}>
      <Icons.Close className={closeButtonClass} />
    </button>
  );
};

const ToastContainer = (): React.JSX.Element => (
  <ToastifyContainer
    style={{
      '--toastify-color-progress-bgo': '0',
    } as React.CSSProperties}
    autoClose={2500}
    draggable
    position="top-right"
    hideProgressBar={false}
    pauseOnHover
    closeOnClick={false}
    className={toastStyles.container}
    toastClassName={(toast) =>
      getToastTypeStyles(toast?.defaultClassName, toast?.type, 'component')
    }
    bodyClassName={toastStyles.body}
    closeButton={(props) => <CustomCloseButton closeToast={props.closeToast} type={props.type} />}
    progressClassName={(toast) =>
      getToastTypeStyles(toast?.defaultClassName, toast?.type, 'progressBar')
    }
  />
);

export default ToastContainer;
